import { z } from "zod"

export type ConfirmationConfig = z.infer<typeof confirmationSchema>
export type ButtonConfig<T extends z.ZodTypeAny> = z.infer<ReturnType<typeof actionButtonSchema<T>>>
export type DefaultAction = z.infer<typeof defaultActionSchema>

const notificationSchema = z.object({
  onSuccess: z.string().optional(),
  onFailure: z.string().optional(),
})

const startRuleActionSchema = z.object({
  type: z.literal('start-rule'),
  startRule: z.string(),
  parameters: z.record(z.string(), z.string()).optional(),
  timestampVariable: z.string().optional(),
})

const publishAppVarSchema = z.object({
  type: z.literal('publish'),
  topic: z.string(),
  value: z.string(),
})

const fetchFileSchema = z.object({
  type: z.literal('fetch-file'),
  url: z.string(),
  desiredFilename: z.string(),
})

const queryToFileSchema = z.object({
  type: z.literal('query-to-file'),
  constructQuery: z.string(),
  desiredFilename: z.string(),
  mediaType: z.literal('text/turtle'),
})

const cancelJobSchema = z.object({
  type: z.literal('cancel-job'),
  jobId: z.string(),
  gracefulCancellationTimeout: z.string().optional()
})

const nativeActionSchema = z.object({
  type: z.literal("native"),
  action: z.string(),
  data: z.record(z.string(), z.any()).optional()
})

const baseActionSchema = z.object({
  notification: notificationSchema.optional(),
})

const confirmationSchema = z.object({
  message: z.string().optional(),
  yesLabel: z.string().optional(),
  yesIcon: z.string().optional(),
  yesColor: z.string().optional(),
  noLabel: z.string().optional(),
  noIcon: z.string().optional(),
  noColor: z.string().optional(),
})

export const withDefaultActionsSchema = <
  T extends readonly z.ZodDiscriminatedUnionOption<"type">[]
>(
  ...extraActions: T
) =>
  z.discriminatedUnion("type", [
      startRuleActionSchema,
      publishAppVarSchema,
      fetchFileSchema,
      queryToFileSchema,
      cancelJobSchema,
      nativeActionSchema,
      ...extraActions
    ])
    .and(baseActionSchema)

export const defaultActionSchema = withDefaultActionsSchema()

export type ConfigurableButton<T extends z.ZodTypeAny> = ButtonCfg<T> | DropdownCfg<T> | SpacerCfg | SeparatorCfg | HeaderCfg

export type ButtonCfg<T extends z.ZodTypeAny> = {
  icon?: string
  label?: string
  tooltip?: string
  actions?: z.infer<T>[]
  buttons?: ConfigurableButton<T>[]
  confirmation?: z.infer<typeof confirmationSchema>
  hidden?: boolean | string
  disabled?: boolean | string
}
export type DropdownCfg<T extends z.ZodTypeAny> = {
  icon?: string
  label?: string
  tooltip?: string
  buttons: ConfigurableButton<T>[]
  hidden?: boolean | string
  disabled?: boolean | string
}
export type SpacerCfg = { spacer: boolean }
export type SeparatorCfg = { separator: boolean }
export type HeaderCfg = {
  header: boolean
  logo?: string
  title?: string
  color?: string
}

export function actionButtonSchema<T extends z.ZodTypeAny>(actionSchema: T) {
  const schema: z.ZodType<ConfigurableButton<T>> = z.lazy(() =>
    // not z.union, as it gives parsing issues
    z.object({
      icon: z.string().optional(),
      label: z.string().optional(),
      tooltip: z.string().optional(),
      actions: z.array(actionSchema).optional(),
      buttons: z.array(schema).optional(),
      confirmation: confirmationSchema.optional(),
      hidden: z.union([z.boolean(), z.string()]).optional(),
      disabled: z.union([z.boolean(), z.string()]).optional(),
      separator: z.boolean().optional(),
      spacer: z.boolean().optional(),
      header: z.boolean().optional(),
      logo: z.string().optional(),
      title: z.string().optional(),
      color: z.string().optional(),
    })
  )
  return schema
}

export function isButton<T extends z.ZodTypeAny>(b: ConfigurableButton<T>): b is ButtonCfg<T> {
  return !("buttons" in b) && ("icon" in b || "label" in b || "tooltip" in b || "actions" in b)
}

export function isDropdown<T extends z.ZodTypeAny>(b: ConfigurableButton<T>): b is DropdownCfg<T> {
  return "buttons" in b
}

export function isSpacer<T extends z.ZodTypeAny>(b: ConfigurableButton<T>): b is SpacerCfg {
  return "spacer" in b && b.spacer
}

export function isSeparator<T extends z.ZodTypeAny>(b: ConfigurableButton<T>): b is SeparatorCfg {
  return "separator" in b && b.separator
}

export function isHeader<T extends z.ZodTypeAny>(b: ConfigurableButton<T>): b is HeaderCfg {
  return "header" in b && b.header
}
